import { Checkbox, FormControlLabel, Grid, useEventCallback } from '@mui/material';
import PmiTextField from 'shell/components/shared/text-field';
import ListGender from 'shell/components/shared/gender';
import { DatePicker, TimePicker } from '@mui/lab';
import { memo, useMemo } from 'react';
import { FormikErrors, FormikTouched } from 'formik';
import { DocumentDeathFormVM } from 'civil-status/view-models/document-birth-form';
import MatricolaList from './matricola-list';
import { PageMode } from 'civil-status/view-models/page-mode';
import ListCivilStatus from './list-civil-status';

interface DocumentDeathFormProps {
  values?: DocumentDeathFormVM;
  errors?: FormikErrors<DocumentDeathFormVM>;
  touched?: FormikTouched<DocumentDeathFormVM>;
  onChange: (
    key: keyof DocumentDeathFormVM,
    value: DocumentDeathFormVM[keyof DocumentDeathFormVM]
  ) => void;
  onChanges: (values: DocumentDeathFormVM) => void;
  mode: PageMode;
}

const DocumenDeathFormComponent = ({
  values,
  touched,
  errors,
  mode,
  onChange,
  onChanges,
}: DocumentDeathFormProps): React.ReactElement => {
  const disabled = useMemo(() => {
    return mode === PageMode.Detail;
  }, [mode]);

  const handleCivilStatusListChange = useEventCallback((element: string | undefined) => {
    onChange('status', element);
  });

  return (
    <>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <MatricolaList
            aria-label="Matricola"
            label="Matricola"
            id="matricola"
            disabled={disabled}
            surname={values?.surname || ''}
            name={values?.name || ''}
            value={values?.matricola || ''}
            dateBirth={values?.birthday || null}
            onChanges={(value) => onChanges(value)}
            error={touched?.matricola && Boolean(errors?.matricola)}
            isSmallSize
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <PmiTextField
            aria-label="Cognome"
            label={`Cognome ${mode === PageMode.Edit ? '*' : ''}`}
            id="surname"
            name="surname"
            fullWidth
            disabled={disabled}
            value={values?.surname || ''}
            onChange={(event) => onChange('surname', event?.target.value)}
            error={touched?.surname && Boolean(errors?.surname)}
            helperText={errors?.surname}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <PmiTextField
            aria-label="Nome"
            label={`Nome ${mode === PageMode.Edit ? '*' : ''}`}
            id="name"
            name="name"
            fullWidth
            disabled={disabled}
            value={values?.name || ''}
            onChange={(event) => onChange('name', event?.target.value)}
            error={touched?.name && Boolean(errors?.name)}
            helperText={errors?.name}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }} md={7}>
          <ListGender
            value={values?.gender || ''}
            onChange={(event) => onChange('gender', event?.target.value)}
            error={touched?.gender && Boolean(errors?.gender)}
            disabled={disabled}
            isSmallSize
          />
        </Grid>
        <Grid item xs={12} style={{ paddingTop: '10px' }} md={5}>
          <FormControlLabel
            control={
              <Checkbox
                id="deathPresumed"
                checked={values?.deathPresumed || false}
                onChange={() =>
                  onChanges({
                    ...values,
                    deathDate: null,
                    hourDeathDate: null,
                    deathBy: null,
                    deathTo: null,
                    deathPresumed: !values?.deathPresumed,
                  })
                }
                inputProps={{ 'aria-label': 'morte presunta' }}
                disabled={mode === PageMode.Detail}
              />
            }
            label="Morte Presunta"
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }} md={6}>
          <DatePicker
            label="Data morte"
            aria-label="Data morte"
            value={values?.deathDate}
            disabled={disabled || Boolean(values?.deathBy || values?.deathTo)}
            onChange={(value) =>
              onChanges({
                ...values,
                deathDate: value,
                deathBy: null,
                deathTo: null,
              })
            }
            renderInput={(params) => (
              <PmiTextField
                id="deathDate"
                name="deathDate"
                {...params}
                fullWidth
                error={touched?.deathDate && Boolean(errors?.deathDate)}
                helperText={errors?.deathDate}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} style={{ paddingTop: '10px' }} md={6}>
          <TimePicker
            label="Ora morte"
            value={values?.hourDeathDate}
            disabled={disabled || values?.hourDeathUnknown}
            onChange={(value) => onChange('hourDeathDate', value)}
            renderInput={(params) => (
              <PmiTextField
                id="hourDeathDate"
                name="hourDeathDate"
                {...params}
                fullWidth
                error={touched?.hourDeathDate && Boolean(errors?.hourDeathDate)}
                helperText={errors?.hourDeathDate}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} style={{ paddingTop: '10px' }} md={6}>
          <FormControlLabel
            control={
              <Checkbox
                id="hourDeathUnknown"
                checked={values?.hourDeathUnknown || false}
                onChange={() =>
                  onChanges({
                    ...values,
                    hourDeathDate: null,
                    hourDeathUnknown: !values?.hourDeathUnknown,
                  })
                }
                inputProps={{ 'aria-label': 'ore imprecisate' }}
                disabled={mode === PageMode.Detail}
              />
            }
            label="Ore imprecisate"
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }} md={6}>
          <DatePicker
            label="Morto tra"
            aria-label="Morto tra"
            value={values?.deathBy}
            disabled={disabled || Boolean(values?.deathDate)}
            onChange={(value) =>
              onChanges({
                ...values,
                deathDate: null,
                hourDeathDate: null,
                deathBy: value,
              })
            }
            renderInput={(params) => (
              <PmiTextField
                id="deathBy"
                name="deathBy"
                {...params}
                fullWidth
                error={touched?.deathBy && Boolean(errors?.deathBy)}
                helperText={errors?.deathBy}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} style={{ paddingTop: '10px' }} md={6}>
          <DatePicker
            label="e il"
            aria-label="e il"
            value={values?.deathTo}
            disabled={disabled || Boolean(values?.deathDate)}
            onChange={(value) =>
              onChanges({
                ...values,
                deathDate: null,
                hourDeathDate: null,
                deathTo: value,
              })
            }
            renderInput={(params) => (
              <PmiTextField
                id="deathTo"
                name="deathTo"
                {...params}
                fullWidth
                error={touched?.deathTo && Boolean(errors?.deathTo)}
                helperText={errors?.deathTo}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <PmiTextField
            aria-label="Comune morte"
            label="Comune morte"
            id="deathMunicipality"
            name="deathMunicipality"
            fullWidth
            disabled={disabled}
            value={values?.deathMunicipality || ''}
            onKeyDown={(event) => {
              if (event.code === 'F2') {
                event.preventDefault();
                event.stopPropagation();
                onChange('deathMunicipality', 'MILANO');
                return;
              }
            }}
            onChange={(event) => {
              onChange('deathMunicipality', event?.target.value);
            }}
            error={touched?.deathMunicipality && Boolean(errors?.deathMunicipality)}
            helperText={errors?.deathMunicipality}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} md={9} style={{ paddingTop: '10px' }}>
          <PmiTextField
            aria-label="Comune residenza"
            label="Comune residenza"
            id="residenceMunicipality"
            name="residenceMunicipality"
            fullWidth
            disabled={disabled}
            value={values?.residenceMunicipality || ''}
            onKeyDown={(event) => {
              if (event.code === 'F2') {
                event.preventDefault();
                event.stopPropagation();
                onChange('residenceMunicipality', 'MILANO');
                return;
              }
            }}
            onChange={(event) => {
              onChange('residenceMunicipality', event?.target.value);
            }}
            error={touched?.residenceMunicipality && Boolean(errors?.residenceMunicipality)}
            helperText={errors?.residenceMunicipality}
          />
        </Grid>
        <Grid item xs={12} md={3} style={{ paddingTop: '10px' }}>
          <span>F2 per Milano</span>
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} md={4} style={{ paddingTop: '10px' }}>
          <DatePicker
            label="Data nascita"
            aria-label="Data nascita"
            value={values?.birthday || null}
            disabled={disabled}
            onChange={(value) => onChange('birthday', value)}
            renderInput={(params) => (
              <PmiTextField
                id="birthday"
                name="birthday"
                {...params}
                fullWidth
                error={touched?.birthday && Boolean(errors?.birthday)}
                helperText={errors?.birthday}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4} style={{ paddingTop: '10px' }}>
          <PmiTextField
            aria-label="Anni"
            label="Anni"
            id="year"
            name="years"
            fullWidth
            disabled={disabled}
            value={values?.years || ''}
            onChange={(event) => onChange('years', event?.target.value)}
            error={touched?.years && Boolean(errors?.years)}
            helperText={errors?.years}
          />
        </Grid>
        <Grid item xs={12} md={4} style={{ paddingTop: '10px' }}>
          <PmiTextField
            aria-label="Anno nascita"
            label="Anno nascita"
            id="year-birth"
            name="year-birth"
            fullWidth
            disabled={disabled}
            value={values?.yearDate || ''}
            onChange={(event) => onChange('yearDate', event?.target.value)}
            error={touched?.yearDate && Boolean(errors?.yearDate)}
            helperText={errors?.yearDate}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} md={7} style={{ paddingTop: '10px' }}>
          <PmiTextField
            aria-label="Comune nascita"
            label="Comune nascita"
            id="municipalityBirth"
            name="municipalityBirth"
            fullWidth
            disabled={disabled}
            value={values?.municipalityBirth || ''}
            onKeyDown={(event) => {
              if (event.code === 'F2') {
                event.preventDefault();
                event.stopPropagation();
                onChange('municipalityBirth', 'MILANO');
                return;
              }
            }}
            onChange={(event) => {
              onChange('municipalityBirth', event?.target.value);
            }}
            error={touched?.municipalityBirth && Boolean(errors?.municipalityBirth)}
            helperText={errors?.municipalityBirth}
          />
        </Grid>
        <Grid item xs={12} md={5} style={{ paddingTop: '10px' }}>
          <ListCivilStatus
            id="civil-status"
            value={values?.status || ''}
            disabled={disabled}
            onChange={(event) => handleCivilStatusListChange(event.target.value)}
            error={touched?.status && Boolean(errors?.status)}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <PmiTextField
            label="Coniuge - Cognome"
            aria-label="Coniuge - Cognome"
            id="spouseSurname"
            name="spouseSurname"
            fullWidth
            value={values?.spouseSurname || ''}
            disabled={disabled}
            onChange={(event) => onChange('spouseSurname', event?.target.value)}
            error={touched?.spouseSurname && Boolean(errors?.spouseSurname)}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <PmiTextField
            label="Coniuge - Nome"
            aria-label="Coniuge - Nome"
            id="spouseName"
            name="spouseName"
            fullWidth
            value={values?.spouseName || ''}
            disabled={disabled}
            onChange={(event) => onChange('spouseName', event?.target.value)}
            error={touched?.spouseName && Boolean(errors?.spouseName)}
          />
        </Grid>
      </Grid>
      {/* <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <PmiTextField
            
            aria-label="Padre - Cognome"
            label="Padre - Cognome"
            id="surnameFather"
            name="surnameFather"
            fullWidth
            disabled={disabled}
            value={values?.surnameFather || ''}
            onChange={(event) => onChange('surnameFather', event?.target.value)}
            error={touched?.surnameFather && Boolean(errors?.surnameFather)}
            helperText={errors?.surnameFather}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <PmiTextField
            
            aria-label="Padre - Nome"
            label="Padre - Nome"
            id="nameFather"
            name="nameFather"
            fullWidth
            disabled={disabled}
            value={values?.nameFather || ''}
            onChange={(event) => onChange('nameFather', event?.target.value)}
            error={touched?.nameFather && Boolean(errors?.nameFather)}
            helperText={errors?.nameFather}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <PmiTextField
            
            aria-label="Madre - Cognome"
            label="Madre - Cognome"
            id="surnameMother"
            name="surnameMother"
            fullWidth
            disabled={disabled}
            value={values?.surnameMother || ''}
            onChange={(event) => onChange('surnameMother', event?.target.value)}
            error={touched?.surnameMother && Boolean(errors?.surnameMother)}
            helperText={errors?.surnameMother}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} style={{ paddingTop: '10px' }}>
          <PmiTextField
            
            aria-label="Madre - Nome"
            label="Madre - Nome"
            id="nameMother"
            name="nameMother"
            fullWidth
            disabled={disabled}
            value={values?.nameMother || ''}
            onChange={(event) => onChange('nameMother', event?.target.value)}
            error={touched?.nameMother && Boolean(errors?.nameMother)}
            helperText={errors?.nameMother}
          />
        </Grid>
      </Grid> */}
    </>
  );
};

DocumenDeathFormComponent.displayName = 'Document Death form component';

const DocumentDeathFormShared = memo(DocumenDeathFormComponent);

export default DocumentDeathFormShared;
